class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isInitialClick = true;
    this._onFormSubmit = this.onFormSubmit.bind(this);
    this._onFocus       = this.onFocus.bind(this);
    this._onFocusOut    = this.onFocusOut.bind(this);
    this._onKeyup       = this.onKeyup.bind(this);
    this._onKeydown     = this.onKeydown.bind(this);
    this._onBackClick   = this.handleMobileBackButtonClick.bind(this);

    this.setupEventListeners();
    this.attachUpsellCloseListeners();
    this.setupUpsellTouchListener();
    this._onMobileResizeSetMaxHeight = this._onMobileResizeSetMaxHeight.bind(this);
    window.addEventListener('resize', this._onMobileResizeSetMaxHeight);
    window.addEventListener('orientationchange', this._onMobileResizeSetMaxHeight);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this._onFormSubmit);
    this.input.addEventListener('focus', this._onFocus);
    this.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('keyup', this._onKeyup);
    this.addEventListener('keydown', this._onKeydown);
    const container = this.getClosestSearchContainer();
    if (container) {
      const backButton = container.querySelector('.header-search-bar-mobile-back__button');
      if (backButton) {
        backButton.addEventListener('click', this._onBackClick);
      }
    }
  }

  disconnectedCallback() {
    this.input.form.removeEventListener('submit', this._onFormSubmit);
    this.input.removeEventListener('focus', this._onFocus);
    this.removeEventListener('focusout', this._onFocusOut);
    this.removeEventListener('keyup', this._onKeyup);
    this.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onMobileResizeSetMaxHeight.bind(this));
    window.removeEventListener('orientationchange', this._onMobileResizeSetMaxHeight.bind(this));
    const container = this.getClosestSearchContainer();
    if (container) {
      const backButton = container.querySelector('.header-search-bar-mobile-back__button');
      if (backButton) {
        backButton.removeEventListener('click', this._onBackClick);
      }
    }
    super.disconnectedCallback && super.disconnectedCallback();
  }

  handleMobileBackButtonClick() {
    if (this.input) {
      this.input.blur();
      this.input.value = '';
    }

    this.close(true);
    super.toggleResetButton();

    // Hide upsell
    const container = this.getClosestSearchContainer();
    if (container && container.classList.contains('header-search-bar-mobile-container')) {
      const upsellWrapper = container.querySelector('.header-search-bar-upsell__wrapper');
      if (upsellWrapper && !upsellWrapper.classList.contains('hidden')) {
        upsellWrapper.classList.add('hidden');
      }
    }

    // Reset initial click flag if you use it
    this.isInitialClick = true;

    // Hide the back button itself
    this.setMobileBackButtonVisible(false);
  }

  setMobileBackButtonVisible(visible) {
    const container = this.getClosestSearchContainer();
    if (!container) return;

    const backButton = container.querySelector('.header-search-bar-mobile-back__button');
    if (!backButton) return;

    const searchModalContent = container.querySelector('.search-modal__content');
    if (!searchModalContent) return;

    if (visible) {
      backButton.classList.remove('hidden');
      searchModalContent.classList.add('with-back-button');
    } else {
      backButton.classList.add('hidden');
      searchModalContent.classList.remove('with-back-button');
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    this.toggleUpsellHidden(!newSearchTerm.length);
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    const upsellHidden = this.isUpsellHidden();

    this.toggleUpsellHidden(!currentSearchTerm.length);

    const isMobile = window.matchMedia('(max-width: 990px)').matches;
    // Event data to pass to analytics
    const eventData = {
      "Page name": document.body.id,
      "Page url": window.location.href,
    };
    if (isMobile) {
      if (!currentSearchTerm.length && upsellHidden) {
        // Show the back button on initial empty focus
        this.setMobileBackButtonVisible(true);

        // your existing blur + upsell show logic
        this.toggleUpsellHidden(true);
        this.input.blur();
        if (typeof clevertap !== 'undefined' && clevertap.event) {
          clevertap.event.push('search_initiated', eventData);
        }
        return;
      }
      
      // Show back button if predictive search results or upsell visible
      if (!upsellHidden || this.hasAttribute('results')) {
        this.setMobileBackButtonVisible(true);
      }
    }
    
    // Hide back button otherwise (if input has text and no upsell/results)
    else {
      this.setMobileBackButtonVisible(false);
      if (typeof clevertap !== 'undefined' && clevertap.event) {
        clevertap.event.push('search_initiated', eventData);
      }
    }

    if (!currentSearchTerm.length) return;

    // existing logic for search
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }


  _onMobileResizeSetMaxHeight() {
    if (!this.isOpen) return; // Only update if results are open
    if (window.matchMedia('(max-width: 990px)').matches) {
      this.setPredictiveResultsMaxHeight();
    }
  }

  isUpsellHidden() {
    const container = this.getClosestSearchContainer();
    if (!container) return true; // assume hidden if container not found
    const upsellWrapper = container.querySelector('.header-search-bar-upsell__wrapper');
    if (!upsellWrapper) return true;
    return upsellWrapper.classList.contains('hidden');
  }


  onFocusOut() {
    setTimeout(() => {
      // if (!this.contains(document.activeElement)) this.close();   
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.header-wrapper--border-bottom')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  setPredictiveResultsMaxHeight() {
    // Only run on mobile (width <= 990px)
    if (!window.matchMedia('(max-width: 990px)').matches) return;

    const container = this.predictiveSearchResults;
    if (!container) return;

    // Adjust for any fixed header if necessary
    let offset = 0;
    const header = document.querySelector('.header-wrapper--border-bottom');
    if (header) {
      offset = header.getBoundingClientRect().bottom;
    }

    // Set maxHeight to the current viewport height minus header offset
    container.style.maxHeight = (window.innerHeight - offset) + 'px';
  }


  open() {
    // Only apply dynamic maxHeight on mobile
    if (window.matchMedia('(max-width: 990px)').matches) {
      this.setPredictiveResultsMaxHeight();
    } else {
      // Optionally, reset maxHeight for desktop if you want
      this.predictiveSearchResults.style.maxHeight = '';
    }
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  getClosestSearchContainer() {
    const isMobile = window.matchMedia('(max-width: 990px)').matches;
    const selector = isMobile
      ? '.header-search-bar-mobile-container'
      : '.header-search-bar-desktop-container';
    // 'this' refers to predictive-search element here
    return this.closest(selector);
  }

  setupUpsellTouchListener() {
    const container = this.getClosestSearchContainer();
    if (!container) return;

    // Only proceed if container is mobile version
    if (!container.classList.contains('header-search-bar-mobile-container')) return;

    // List of all class names you want to add the touch handler to
    const touchTargets = [
      '.predictive-search--header',
      '.header-search-bar-upsell__wrapper',
      // Add more selectors here as needed, e.g. '.my-another-touch-class'
    ];

    // Remove existing handlers (optional and advanced, depending on handler scope)
    // This snippet assumes this._upsellTouchHandler is simple and always safe to remove/add.

    // Define the touch handler as a bound method so it can be referenced/removed if needed
    if (!this._upsellTouchHandler) {
      this._upsellTouchHandler = () => {
        const inputs = document.querySelectorAll('.search__input, .field__input');
        inputs.forEach(input => {
          if (document.activeElement === input) {
            input.blur();
          }
        });
      };
    }

    // Find all elements in the container that match any of the selectors
    touchTargets.forEach(selector => {
      const elements = container.querySelectorAll(selector);
      elements.forEach(element => {
        // Remove previous listener, if any
        element.removeEventListener('touchstart', this._upsellTouchHandler);

        // Add passive touch event listener
        element.addEventListener('touchstart', this._upsellTouchHandler, { passive: true });
      });
    });
  }

  toggleUpsellHidden(show) {
    const container = this.getClosestSearchContainer();
    if (!container) return;
    const upsellWrapper = container.querySelector('.header-search-bar-upsell__wrapper');
    if (!upsellWrapper) return;

    if (show) {
      if (upsellWrapper.classList.contains('hidden')) {
        void upsellWrapper.offsetWidth;
        upsellWrapper.classList.remove('hidden');
        setTimeout(() => {
        const sliders = upsellWrapper.querySelectorAll('carousel-component');
        sliders.forEach(sliderElement => {
          // Check if the method exists and call refreshSlider
          if (typeof sliderElement.refreshSlider === 'function') {
            sliderElement.refreshSlider();
          }
        });
      }, 50);
      }
    } else {
      if (!upsellWrapper.classList.contains('hidden')) {
        upsellWrapper.classList.add('hidden');
      }
    }
  }

  attachUpsellCloseListeners() {
    // For desktop
    const desktopContainer = document.querySelector('.header-search-bar-desktop-container');
    const cartNotificationWrapper = document.querySelector('.cart-notification-wrapper');
    if (desktopContainer) {
      const desktopWrapper = desktopContainer.querySelector('.header-search-bar-upsell__wrapper');
      const desktopClose = desktopWrapper?.querySelector('.header-search-upsell__close-button');
      if (desktopClose && !desktopClose._upsellCloseHandlerAdded) {
        desktopClose.addEventListener('click', () => {
          desktopWrapper.classList.add('hidden');
          this.isInitialClick = true;
        });
        desktopClose._upsellCloseHandlerAdded = true;
      }

      // Document listener for outside clicks (desktop)
      if (!desktopWrapper._outsideClickHandlerAdded) {
        document.addEventListener('click', (e) => {
          if (
            ((desktopWrapper && !desktopWrapper.classList.contains('hidden')) || this.hasAttribute('results')) &&
            !desktopWrapper.contains(e.target) &&
            !desktopContainer.contains(e.target) &&
            !cartNotificationWrapper.contains(e.target)
          ) {
            // Hide upsell wrapper only if visible
            if (desktopWrapper && !desktopWrapper.classList.contains('hidden')) {
              desktopWrapper.classList.add('hidden');
            }

            // Hide search results if results attribute exists
            if (this.hasAttribute('results')) {
              this.closeResults(true);
              this.input.value = '';
              this.input.blur();
              this.input.form.reset();
              this.toggleResetButton();
            }

            // Keep upsell hidden after closing results
            this.toggleUpsellHidden(false);

            this.isInitialClick = true;
            this.input.blur();
          }
        });
        desktopWrapper._outsideClickHandlerAdded = true;
      }
    }

    // For mobile
    const mobileContainer = document.querySelector('.header-search-bar-mobile-container');
    if (mobileContainer) {
      const mobileWrapper = mobileContainer.querySelector('.header-search-bar-upsell__wrapper');
      const mobileClose = mobileWrapper?.querySelector('.header-search-upsell__close-button');
      if (mobileClose && !mobileClose._upsellCloseHandlerAdded) {
        mobileClose.addEventListener('click', () => {
          mobileWrapper.classList.add('hidden');
          this.isInitialClick = true;
        });
        mobileClose._upsellCloseHandlerAdded = true;
      }
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

